import { wallOpeningDepth, wallOpeningHasExteriorSide } from "building-common/openings/openingQuery";
import { profile2OrientCopy } from "building-common/profile2Utils";
import { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import { WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import type { Asset } from "building-types/entities/asset";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { OpeningShapes } from "building-types/openings/openingTypes";
import type { PropAttributeTable } from "building-types/props/propTypes";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import { AwningType, SideCasingType, WindowMullionType } from "building-types/settings/settingsTypesEnum";
import type { AllWindowViewNoteTable } from "building-types/settings/viewNotesSettings";
import { WindowViewNoteType } from "building-types/settings/viewNotesSettings";
import type { WainscotingSettings } from "building-types/settings/wainscotingSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import type { GridWindowMullionSettings } from "building-types/settings/windowMullionSettings";
import {
  WindowPaneType,
  WindowShapeTypes,
  WindowPanelType,
  WindowOperationType,
  WindowVertDivisions,
  type GlazingColor,
} from "building-types/settings/windowTypesEnum";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import type { WallSideContext } from "building-types/walls/wallSideTypes";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type {
  WindowUnit,
  GeneratedWindowPlacement,
  MergedWindowSettings,
  WindowSettingsSrc,
} from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { ensureDefined } from "common/utils/assertOfType";
import { degreeToRadian } from "common/utils/math";
import uid from "common/utils/uid";
import { WINDOW_EXTERIOR_FRAME_DEPTH, WINDOW_INTERIOR_FRAME_WIDTH } from "doc/elements/window/constants";
import { roomIsEffectivelyExterior } from "doc/rooms/roomQuery";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import { exteriorMaterialOverrideNew, interiorMaterialOverrideNew } from "doc/utils/materialTools";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { aabb2Center } from "geom/aabb2";
import type { Extrusion } from "geom/brep";
import { faceNew } from "geom/brep";
import type { NodeBSP } from "geom/bspTypes";
import { DIST_EPSILON, DIST_Z_FIGHT } from "geom/core";
import { extrudeFaceAlongNormal, extrusionToBRep } from "geom/extrude";
import GeomTagGroup from "geom/geomTagGroup";
import { offsetPolyLine2, offsetPolyLine2SSkFallback, polyLine2Thicken } from "geom/offset";
import { planeMoveAlongNormal, planeCopy, planeNormal } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2Area,
  polyLine2Edge,
  polyLine2Move,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2ReverseCopy,
  polyLine2AABB2,
  polyLine2EdgeLength,
  polyLine2Start,
  polyLine2End,
  polyLine2ConcatAll,
  polyLine2SliceByStartEnd,
  polyLine2Reverse,
  polyLine2ByTwoPoints,
  polyLine2SetCW,
  polyLine2EdgeEval,
  polyLine2SetCCW,
  polyLine2Copy,
  polyLine2NonUniformScaleAndKeepCenter,
  polyLine2Reflect,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { rangeNonInclusiveByMinStep } from "geom/range";
import { ray2New } from "geom/ray2";
import { rect2UniformMultiSplit, rect2MultiSplitFlex, rect2Split } from "geom/rect2";
import { addAwning } from "../awnings";
import { entityLateralOffsetFromWallDatum } from "../openings/openingWallLayers";
import { checkOrientationWithMode } from "../streetOrientation";
import { windowShapes, windowShapesCopy } from "./windowElevation";
import { makeExteriorWindowCasing } from "./windowExteriorCasing";
import { getWindowHeight } from "./windowHeightTools";
import { windowDescriptorShouldCutWall, windowHasCasing } from "./windowQuery";
import { getWindowOperableHeight, WINDOW_FRAME_WIDTH } from "./windowTools";
import type { PaneDescriptor, WindowGenerator } from "./windowUtils";
import {
  windowAlignment,
  WINDOW_FRAME_DEPTH,
  WINDOW_SASH_FRAME_WIDTH,
  splitArcPlRay,
  halfRoundInnerOffset,
  WINDOW_SASH_FRAME_DEPTH_HALF,
  ventExtrusion,
  GLASS_THICKNESS,
  generateWindowPanes,
  WINDOW_JAMB_THICKNESS,
} from "./windowUtils";

function rectMinusArcHeight(rect2: PolyLine2, arcHeight: number = 0): PolyLine2 {
  return polyLine2New([
    rect2[0],
    rect2[1],
    rect2[2],
    rect2[3] - arcHeight,
    rect2[4],
    rect2[5] - arcHeight,
    rect2[6],
    rect2[7],
    rect2[8],
    rect2[9],
  ]);
}

interface WindowGlassExtrusion {
  readonly extrusion: Extrusion;
  readonly exportComponentName: string;
}

const WINDOW_COMPONENT_FULL = "Full";
const WINDOW_COMPONENT_INNER_FRAME = "Inner_Frame";
const WINDOW_COMPONENT_JAMB = "Jamb";
const WINDOW_COMPONENT_OUTER_FRAME = "Outer_Frame";
const WINDOW_COMPONENT_UPPER = "Upper";

function paneTypeComponentName(paneType: WindowPaneType): string {
  switch (paneType) {
    case WindowPaneType.FULL:
      return WINDOW_COMPONENT_FULL;
    case WindowPaneType.TOP:
      return WINDOW_COMPONENT_UPPER;
    case WindowPaneType.LOW:
      return "Lower";
    case WindowPaneType.LEFT:
      return "Left";
    case WindowPaneType.RIGHT:
      return "Right";
  }
}

function verticalDivisionComponentName(divNum: number, index: number): string | undefined {
  if (divNum <= 1) {
    return undefined;
  }

  if (divNum === 2) {
    return index === 0 ? "Left" : "Right";
  }

  if (divNum === 3) {
    if (index === 0) {
      return "Left";
    }
    if (index === 1) {
      return "Center";
    }
    return "Right";
  }

  return `${index + 1}`;
}

function windowPaneComponentName(paneType: WindowPaneType, divNum: number, divisionIndex: number): string {
  const paneName = paneTypeComponentName(paneType);
  const divisionName = verticalDivisionComponentName(divNum, divisionIndex);

  if (!divisionName) {
    return paneName;
  }

  if (paneType === WindowPaneType.FULL) {
    return divisionName;
  }

  if (paneType === WindowPaneType.TOP || paneType === WindowPaneType.LOW) {
    return `${paneName}_${divisionName}`;
  }

  return `${divisionName}_${paneName}`;
}

export function getOperationType(mergedWindowSettings: WindowSettingsSrc | undefined): WindowOperationType {
  if (!mergedWindowSettings) {
    return WindowOperationType.DOUBLE_HUNG;
  }

  const { mullions, operationType: mergedOperationType } = mergedWindowSettings;

  if (!mullions) {
    return WindowOperationType.DOUBLE_HUNG;
  }

  const mullionOperationType =
    mullions.mullionType === WindowMullionType.DISABLED
      ? undefined
      : (mullions as GridWindowMullionSettings).operationType;

  return mergedOperationType ?? mullionOperationType ?? WindowOperationType.DOUBLE_HUNG;
}

export interface MakeWindowUnitOptions {
  window: GeneratedWindowPlacement;
  issues: BuildingGenerationIssue[];
  wallLength: number;
  wallThickness: number;
  wallSettings: WallSettings;
  propCatalog: PropAttributeTable;
  assets: Table<Asset>;
  wainscotingSettings: WainscotingSettings;
  datumAlignmentOffset: number;
  exteriorWallMaterial?: ExteriorMaterial;
  streetOrientation?: StreetOrientation;
  clippingBSP?: NodeBSP;
  mirror?: boolean;
  mergedWindowSettings: MergedWindowSettings;
  context: WallSideContext;
  roomOutlines: RoomOutlines | undefined;
  wallMassSystem: WallMassSystem | undefined;
  useLegacyAttachmentSettings?: boolean;
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined;
  /** Used for roof and dormer windows, which have no rooms to read the hosting instance from. */
  fallbackInstanceId?: string;
}

export async function makeWindowUnit({
  window,
  wallLength,
  wallThickness,
  wallSettings,
  exteriorWallMaterial,
  propCatalog,
  assets,
  wainscotingSettings,
  datumAlignmentOffset,
  streetOrientation = StreetOrientation.FRONT,
  roomOutlines,
  wallMassSystem,
  mirror = false,
  mergedWindowSettings,
  context,
  useLegacyAttachmentSettings,
  entityHandleForSelectingReportingUnit,
  fallbackInstanceId,
}: MakeWindowUnitOptions): Promise<WindowUnit | undefined> {
  const { descriptor, opening } = window;
  const windowAlignmentGeom = windowAlignment(window, wallThickness, datumAlignmentOffset);
  const {
    backRoom,
    frontRoom,
    backFacePlane,
    backOutline3,
    frontFacePlane,
    frontOutline3,
    windowFrameCenterlinePlane,
    depthAlignmentOffset,
  } = windowAlignmentGeom;
  const { backOutlineRect2, frontOutlineRect2 } = windowAlignmentGeom;
  if (!exteriorWallMaterial) {
    exteriorWallMaterial = wallSettings.exteriorWallMaterial;
  }

  if (!backOutlineRect2 || polyLine2Area(backOutlineRect2) < 0.01) {
    // These empty windows showed up on Anniston A, causing issues further down the line in export geometry processing.
    return undefined;
  }

  const hasExteriorCasing = windowHasCasing(
    { descriptor: window.descriptor, streetOrientation },
    mergedWindowSettings.exteriorCasing as ExteriorWindowCasingSettings,
  );

  const {
    mullions,
    exteriorCasing,
    awningSettings,
    verticalDivisionSpacing,
    viewNoteTable,
    interiorCasing,
    materials,
  } = mergedWindowSettings;

  const viewEntityDescriptor = viewEntityDescriptorNew(
    uid(),
    ViewEntityType.WINDOW,
    viewNoteTable[WindowViewNoteType.WINDOW],
  );

  if (!mullions) {
    return undefined;
  }

  const operationType = getOperationType(mergedWindowSettings);

  const glazingColor = materials?.glazingColor;

  const unit = windowUnitNew(
    window,
    streetOrientation,
    depthAlignmentOffset,
    frontRoom,
    backRoom,
    viewNoteTable,
    context,
    mirror,
    glazingColor,
    entityHandleForSelectingReportingUnit,
    fallbackInstanceId,
  );

  if (!backFacePlane || !frontFacePlane || !frontOutlineRect2) {
    return undefined;
  }

  // windowShapes should mirror if window.reverse is true; if mirror is set, reverse it again
  const mirrorOrReverse = mirror ? !window.reverse : window.reverse;

  const backShapes = windowShapes(descriptor, backOutlineRect2, mirrorOrReverse);

  const backShapesCasing = windowShapesCopy(backShapes);

  const frontShapes = windowShapes(descriptor, frontOutlineRect2, mirrorOrReverse);

  const frontShapesCasing = windowShapesCopy(frontShapes);

  const {
    outlineReversed: backOutlineReversed,
    arcCurve,
    arcOutline,
    arcOutlineReversed: archOutlineReversed,
    arcHeight,
    arcAngleRads,
    arcCenterX,
    arcCenterY,
    arcRadius,
  } = backShapesCasing;
  const backOutline = backShapes.outline;
  const exteriorRect2 = backShapes.outlineRectangular;

  if (!exteriorCasing) {
    return undefined;
  }

  const trimExtend = exteriorCasing.sideCasingType === SideCasingType.DISABLED ? 0 : exteriorCasing.sideCasingWidth;

  //make awnings
  const AWNING_DEFAULT_HEIGHT = 12;
  if (awningSettings && awningSettings.awningType !== AwningType.DISABLED) {
    if (!backOutlineReversed) {
      return undefined;
    }
    const awningLine = polyLine2Edge(backOutlineReversed, 2);
    polyLine2Move(awningLine, 0, AWNING_DEFAULT_HEIGHT);
    polyLine2ExtendEnd(awningLine, trimExtend);
    polyLine2ExtendStart(awningLine, trimExtend);

    let resolvedLateralOffset = awningSettings?.awningLateralOffset ?? 0;
    const edgeIndex = window.frontRoom?.edgeIds.indexOf(window.edgeId ?? "");
    if (roomOutlines && wallMassSystem && edgeIndex !== undefined && edgeIndex > -1) {
      resolvedLateralOffset = entityLateralOffsetFromWallDatum({
        room: ensureDefined(window.frontRoom),
        edgeIndex,
        roomOutlines,
        wallMassSystem,
        placement: {
          lateralDatum: awningSettings?.awningLateralDatum ?? WallLateralDatumAnchorAll.ROOM_EDGE,
          lateralOffsetNormalized: false,
          lateralOffset: resolvedLateralOffset,
        },
        resolvedParam: 1,
      });
    }

    await addAwning(
      window.awning,
      window.frontRoom,
      window.backRoom,
      resolvedLateralOffset,
      wallLength,
      awningSettings,
      propCatalog,
      assets,
      backFacePlane,
      awningLine,
      unit.decorations,
      unit.awningRoof,
      unit.props,
      viewNoteTable,
      mirror,
    );
  }

  //make window casing
  if (
    !exteriorCasing.casingOrientation ||
    checkOrientationWithMode(streetOrientation, exteriorCasing.casingOrientation)
  ) {
    if (
      (useLegacyAttachmentSettings && opening && backOutline3 && frontOutline3) ||
      (!useLegacyAttachmentSettings && opening && backOutline3 && frontOutline3 && hasExteriorCasing)
    ) {
      makeExteriorWindowCasing(
        descriptor,
        opening,
        backFacePlane,
        backOutline3,
        frontFacePlane,
        frontOutline3,
        trimExtend,
        streetOrientation,
        wainscotingSettings,
        exteriorCasing,
        viewNoteTable,
        unit,
        backShapesCasing,
        frontShapesCasing,
      );
    }
  }

  if (descriptor.open) {
    return unit;
  }

  const glassExtrusions: WindowGlassExtrusion[] = [];

  if (windowDescriptorShouldCutWall(window)) {
    if (opening?.hasJamb) {
      windowUnitAddJamb(unit);
    }

    if (interiorCasing?.includeCasing) {
      if (!roomIsEffectivelyExterior(window.frontRoom, false)) {
        windowUnitAddInteriorWindowFrame(unit, frontShapes, frontFacePlane, viewEntityDescriptor);
      }
      if (!roomIsEffectivelyExterior(window.backRoom, false)) {
        if (roomIsEffectivelyExterior(window.frontRoom, false)) {
          windowUnitAddInteriorWindowFrame(unit, frontShapes, frontFacePlane, viewEntityDescriptor);
        } else {
          windowUnitAddInteriorWindowFrame(
            unit,
            windowShapes(descriptor, backOutlineRect2, !mirror),
            backFacePlane,
            viewEntityDescriptor,
          );
        }
      }
    }

    //frame from glass plane to exterior face
    const frameFacePlane = planeMoveAlongNormal(planeCopy(windowFrameCenterlinePlane), -WINDOW_FRAME_DEPTH / 2);
    const frameInsetOutline = profile2OrientCopy(
      offsetPolyLine2SSkFallback(polyLine2SetCCW(backOutline), -WINDOW_FRAME_WIDTH),
    );
    const frameInsetRect2 = profile2OrientCopy(offsetPolyLine2(polyLine2SetCCW(exteriorRect2), -WINDOW_FRAME_WIDTH));

    if (!backOutlineReversed || !materials) {
      return undefined;
    }

    const frame = faceNew([backOutlineReversed, frameInsetOutline], frameFacePlane);

    unit.exterior.add(
      extrudeFaceAlongNormal(frame, WINDOW_FRAME_DEPTH),
      exteriorMaterialOverrideNew(
        materials.frameMaterial,
        undefined,
        viewEntityDescriptor,
        undefined,
        WINDOW_COMPONENT_OUTER_FRAME,
      ),
    );

    if (arcOutline && archOutlineReversed) {
      const archFrameInsetOutline = offsetPolyLine2(arcOutline, -WINDOW_FRAME_WIDTH);
      const archFrame = faceNew([archOutlineReversed, archFrameInsetOutline], frameFacePlane);

      unit.exterior.add(
        extrudeFaceAlongNormal(archFrame, WINDOW_FRAME_DEPTH),
        exteriorMaterialOverrideNew(
          materials.frameMaterial,
          undefined,
          viewEntityDescriptor,
          undefined,
          WINDOW_COMPONENT_OUTER_FRAME,
        ),
      );
    }

    const winOutlines: PolyLine2[] = [];
    const frameInsetPlReversed = polyLine2ReverseCopy(frameInsetOutline);
    let frameInsetRectReversed = polyLine2ReverseCopy(frameInsetRect2);

    // use aabb values from the full frame outline to align grid placement on vertically divided arched windows
    const aabb = polyLine2AABB2(frameInsetPlReversed);
    const { minX, maxX, minY: gridMinY } = aabb;
    let { maxY: gridMaxY } = aabb;

    let divNum = 1;
    if (descriptor.verticalDivisions === WindowVertDivisions.TWIN) {
      divNum = 2;
    } else if (descriptor.verticalDivisions === WindowVertDivisions.TRIPLE) {
      divNum = 3;
    }

    if (verticalDivisionSpacing === undefined) {
      return undefined;
    }

    // check if window panes can be made with the number of vertical divisions, division width, and sash width
    const remainingWidth = maxX - minX - (divNum - 1) * verticalDivisionSpacing - 2 * WINDOW_SASH_FRAME_WIDTH * divNum;
    if (remainingWidth < DIST_EPSILON && descriptor.shape === WindowShapeTypes.RECTANGLE_ARCHED_TOP) {
      return undefined;
    }

    if (divNum > 1) {
      const divCasingOutlines: PolyLine2[] = [];
      switch (descriptor.shape) {
        case WindowShapeTypes.RECTANGLE: {
          if (descriptor.arched) {
            // the full rectangular opening includes the arched top,
            // so subtract it here to get the rectangular portion of
            // the window frame
            frameInsetRectReversed = rectMinusArcHeight(frameInsetRectReversed, arcHeight);
          }
          if (verticalDivisionSpacing < DIST_EPSILON) {
            winOutlines.push(...rect2UniformMultiSplit(frameInsetRectReversed, false, divNum));
          } else {
            const divInput: number[] = [];
            for (let i = 0; i < divNum * 2 - 1; i += 1) {
              if (i % 2 === 0) {
                divInput.push(0);
              } else {
                divInput.push(verticalDivisionSpacing);
              }
            }
            const shapeOutlines = rect2MultiSplitFlex(frameInsetRectReversed, divInput, false);

            for (let i = 0; i < divNum * 2 - 1; i += 1) {
              if (i % 2 === 0) {
                winOutlines.push(shapeOutlines[i]);
              } else {
                divCasingOutlines.push(shapeOutlines[i]);
              }
            }
          }
          break;
        }
        case WindowShapeTypes.RECTANGLE_ARCHED_TOP: {
          // split arc geometry into segments
          const bottomEdge = polyLine2Edge(frameInsetPlReversed, -1);
          const totalWidth = polyLine2EdgeLength(bottomEdge, 0);

          // origin as the bottom left corner of the window
          const originX = bottomEdge[2];
          const originY = bottomEdge[3];

          const splitValues: number[] = [];

          // fill splitValues array
          if (verticalDivisionSpacing < DIST_EPSILON) {
            splitValues.push(...Array(divNum - 1).fill(totalWidth / divNum));
          } else {
            const paneWidth = (totalWidth - (divNum - 1) * verticalDivisionSpacing) / divNum;
            for (let i = 0; i < (divNum - 1) * 2; i += 1) {
              if (i % 2 === 0) {
                splitValues.push(paneWidth);
              } else {
                splitValues.push(verticalDivisionSpacing);
              }
            }
          }

          const makeArcPanel = (arcPl: PolyLine2): PolyLine2 => {
            const [asx] = polyLine2Start(arcPl);
            const [aex] = polyLine2End(arcPl);
            return polyLine2ConcatAll([[asx, originY], arcPl, [aex, originY]], true);
          };

          // split arc geometry into segments
          let remainingArcPl = polyLine2SliceByStartEnd(frameInsetPlReversed, 1, -2);

          const arcPanels: PolyLine2[] = [];

          let currParam = 0;
          for (const dist of splitValues) {
            currParam += dist;
            if (!remainingArcPl) {
              break;
            }

            const res = splitArcPlRay(remainingArcPl, originX + currParam, originY, 0, 1);
            if (!res) {
              break;
            }

            const [leftArc, rightArc] = res;
            remainingArcPl = rightArc;
            arcPanels.push(makeArcPanel(leftArc));
          }

          if (remainingArcPl) {
            arcPanels.push(makeArcPanel(remainingArcPl));
          }

          // filter panels into respective arrays
          if (verticalDivisionSpacing < DIST_EPSILON) {
            winOutlines.push(...arcPanels);
          } else {
            for (let i = 0; i < arcPanels.length; i += 1) {
              if (i % 2 === 0) {
                winOutlines.push(arcPanels[i]);
              } else {
                divCasingOutlines.push(arcPanels[i]);
              }
            }
          }

          break;
        }
        case WindowShapeTypes.CIRCLE:
        case WindowShapeTypes.HALF_ROUND:
        case WindowShapeTypes.ELLIPSE:
        default: {
          winOutlines.push(frameInsetPlReversed);
          break;
        }
      }

      for (const outline of divCasingOutlines) {
        const divCasing = faceNew([outline], windowFrameCenterlinePlane);
        unit.exterior.add(
          extrudeFaceAlongNormal(divCasing, 1.5),
          exteriorMaterialOverrideNew(
            materials.integralMullionMaterial,
            undefined,
            undefined,
            undefined,
            WINDOW_COMPONENT_OUTER_FRAME,
          ),
        );
      }
    } else {
      if (descriptor.shape === WindowShapeTypes.RECTANGLE && descriptor.arched) {
        winOutlines.push(rectMinusArcHeight(frameInsetRectReversed, arcHeight));
      } else {
        winOutlines.push(frameInsetPlReversed);
      }
    }

    // arched vents
    const panelType = window.materials?.panelType;
    if (panelType === WindowPanelType.VENT && archOutlineReversed) {
      const archedVentOutline = halfRoundInnerOffset(archOutlineReversed, WINDOW_FRAME_WIDTH);
      if (typeof arcHeight === "number") {
        const archedVentExtrusions = ventExtrusion({
          outline: archedVentOutline,
          plane: windowFrameCenterlinePlane,
          divNum: arcHeight / 2,
          thickness: 2,
          clipAgainstOutline: true,
        });

        unit.exterior.addMany(
          archedVentExtrusions,
          exteriorMaterialOverrideNew(materials.ventMaterial, undefined, undefined, undefined, WINDOW_COMPONENT_UPPER),
        );
      }

      const archedVentFrame = faceNew(
        [archedVentOutline, polyLine2Reverse(halfRoundInnerOffset(archedVentOutline, 1.75))],
        planeMoveAlongNormal(planeCopy(windowFrameCenterlinePlane), -WINDOW_SASH_FRAME_DEPTH_HALF),
      );

      unit.exterior.add(
        extrudeFaceAlongNormal(archedVentFrame, 2.5),
        exteriorMaterialOverrideNew(materials.ventMaterial, undefined, undefined, undefined, WINDOW_COMPONENT_UPPER),
      );
    }

    // arched glass
    if (panelType !== WindowPanelType.VENT && arcOutline && archOutlineReversed) {
      const glass = faceNew([archOutlineReversed], windowFrameCenterlinePlane);
      const glassThickness = extrudeFaceAlongNormal(glass, -GLASS_THICKNESS);
      glassExtrusions.push({ extrusion: glassThickness, exportComponentName: WINDOW_COMPONENT_UPPER });
    }

    // arc radial gris
    if (
      mullions.mullionType !== WindowMullionType.DISABLED &&
      panelType !== WindowPanelType.VENT &&
      arcCurve &&
      arcOutline
    ) {
      const aabb = polyLine2AABB2(arcOutline);
      const [x0] = aabb2Center(aabb);
      const y0 = aabb.minY + WINDOW_FRAME_WIDTH * 0.5;

      if (
        typeof arcAngleRads === "number" &&
        typeof arcCenterY === "number" &&
        typeof arcCenterX === "number" &&
        typeof arcRadius === "number"
      ) {
        const startAngle = -arcAngleRads * 0.5 + Math.PI / 2;
        const endAngle = arcAngleRads * 0.5 + Math.PI / 2;
        const radialDivs = rangeNonInclusiveByMinStep(startAngle, endAngle, degreeToRadian(45));
        for (const radialDiv of radialDivs) {
          const x1 = arcCenterX + Math.cos(radialDiv) * arcRadius;
          const y1 = arcCenterY + Math.sin(radialDiv) * arcRadius;

          const divLine = polyLine2ByTwoPoints(x0, y0, x1, y1);

          const outline = polyLine2SetCW(polyLine2Thicken(divLine, mullions.verticalThickness, true));

          const divCasingPlane = planeMoveAlongNormal(planeCopy(frameFacePlane), 0.75);
          const divCasing = faceNew([outline], divCasingPlane);
          unit.exterior.add(
            extrudeFaceAlongNormal(divCasing, 1.5),
            exteriorMaterialOverrideNew(
              materials.gridMaterial,
              undefined,
              undefined,
              undefined,
              WINDOW_COMPONENT_UPPER,
            ),
          );
        }
      }
    }

    for (let i = 0, li = winOutlines.length; i < li; i++) {
      const subOutline = winOutlines[i];

      // if window panel type is vent
      if (panelType === WindowPanelType.VENT) {
        const ventFrame = faceNew(
          [subOutline, polyLine2Reverse(offsetPolyLine2(subOutline, 1.75))],
          planeMoveAlongNormal(planeCopy(windowFrameCenterlinePlane), -WINDOW_SASH_FRAME_DEPTH_HALF),
        );

        if (typeof arcHeight === "number") {
          const ventExtrusions = ventExtrusion({
            outline: subOutline,
            plane: windowFrameCenterlinePlane,
            divNum: (getWindowHeight(descriptor) - arcHeight) / 2,
            thickness: 2,
          });

          const componentName = verticalDivisionComponentName(divNum, i) ?? WINDOW_COMPONENT_FULL;
          unit.exterior.addMany(
            ventExtrusions,
            exteriorMaterialOverrideNew(materials.ventMaterial, undefined, undefined, undefined, componentName),
          );
          unit.exterior.add(
            extrudeFaceAlongNormal(ventFrame, 2.5),
            exteriorMaterialOverrideNew(materials.ventMaterial, undefined, undefined, undefined, componentName),
          );
        }

        continue;
      }

      const paneDescriptors: PaneDescriptor[] = [];

      switch (operationType) {
        case WindowOperationType.FIXED:
        case WindowOperationType.AWNING:
        case WindowOperationType.CASEMENT: {
          paneDescriptors.push({ outline: subOutline, type: WindowPaneType.FULL });
          break;
        }

        case WindowOperationType.SLIDING: {
          const panes = rect2Split(subOutline, false, 0.5, true);
          paneDescriptors.push(
            ...[
              { outline: panes[0], type: WindowPaneType.LEFT },
              { outline: panes[1], type: WindowPaneType.RIGHT },
            ],
          );
          break;
        }

        case WindowOperationType.SINGLE_HUNG:
        case WindowOperationType.DOUBLE_HUNG:
        default: {
          const paneSplitParam = window.paneSplitParam ?? descriptor.paneSplitParam ?? 0.5;
          const paneSplitParamNormalized =
            window.paneSplitParamNormalized ?? descriptor.paneSplitParamNormalized ?? true;

          const resolvedPaneSplitParam = paneSplitParamNormalized
            ? paneSplitParam * getWindowOperableHeight(descriptor)
            : paneSplitParam;

          const rightEdgeLength = polyLine2EdgeLength(subOutline, -2);

          const [leftMidX, leftMidY] = polyLine2EdgeEval(subOutline, 0, resolvedPaneSplitParam, false);
          const [rightMidX, rightMidY] = polyLine2EdgeEval(
            subOutline,
            -2,
            rightEdgeLength - resolvedPaneSplitParam,
            false,
          );
          const outlineLen = subOutline.length;
          const topTopLine = subOutline.slice(2, outlineLen - 4);
          if (descriptor.shape === WindowShapeTypes.RECTANGLE && descriptor.arched) {
            gridMaxY = Math.min(gridMaxY, topTopLine[1]);
          }

          const topOutline = polyLine2ConcatAll([
            [leftMidX, leftMidY],
            topTopLine,
            [rightMidX, rightMidY],
            [leftMidX, leftMidY],
          ]);
          const bottomOutline = polyLine2ConcatAll([
            [subOutline[0], subOutline[1]],
            [leftMidX, leftMidY],
            [rightMidX, rightMidY],
            subOutline.slice(outlineLen - 4, outlineLen),
          ]);

          paneDescriptors.push({ outline: topOutline, type: WindowPaneType.TOP });
          paneDescriptors.push({ outline: bottomOutline, type: WindowPaneType.LOW });
        }
      }

      const namedPaneDescriptors = paneDescriptors.map((paneDescriptor) => ({
        ...paneDescriptor,
        exportComponentName: windowPaneComponentName(paneDescriptor.type, divNum, i),
      }));

      const windowGens: WindowGenerator[] = generateWindowPanes({
        shape: descriptor.shape,
        gridMinY,
        gridMaxY,
        paneDescriptors: namedPaneDescriptors,
        plane: windowFrameCenterlinePlane,
        mullionSettings: mullions,
        streetOrientation,
      });

      windowGens.forEach((gen) => {
        unit.exterior.addMany(
          gen.sashFrame,
          exteriorMaterialOverrideNew(materials.sashMaterial, undefined, undefined, undefined, gen.exportComponentName),
        );
        unit.grids.addMany(
          gen.mullions,
          exteriorMaterialOverrideNew(materials.gridMaterial, undefined, undefined, undefined, gen.exportComponentName),
        );
        glassExtrusions.push(
          ...gen.glass.map((extrusion) => ({ extrusion, exportComponentName: gen.exportComponentName })),
        );
      });
    }
  }

  // right now screens are windows with glass swapped out for screens
  const panelType = window.materials?.panelType;
  if (panelType === WindowPanelType.SCREEN) {
    for (const { extrusion, exportComponentName } of glassExtrusions) {
      unit.screen.push(extrusion);
      unit.screenExportComponentNames.push(exportComponentName);
    }
  } else {
    const [towardExteriorNX, towardExteriorNY, towardExteriorNZ] = planeNormal(frontFacePlane);
    for (const { extrusion, exportComponentName } of glassExtrusions) {
      addGlassExtrusionToUnit(
        extrusion,
        towardExteriorNX,
        towardExteriorNY,
        towardExteriorNZ,
        unit,
        exportComponentName,
      );
    }
  }

  return unit;
}

function windowUnitAddJamb(unit: WindowUnit) {
  const { opening } = unit.windowPlacement;
  if (!opening) {
    return;
  }

  const jambDepth = wallOpeningDepth(opening);
  if (jambDepth < DIST_EPSILON) {
    return;
  }

  const { backPlane, back: backProfile } = opening;

  /**
   * wall finishes have 0 thickness by default, and don't take into
   * account rough openings for windows/doors. We need to adjust this number to prevent
   * z-fighting, until our default finishes have thickness.
   * @TODO - remove this tolerance when all finishes have thickness
   */
  const jambDepthTolerance = DIST_Z_FIGHT / 2;
  const jambPlane = planeMoveAlongNormal(planeCopy(backPlane), -jambDepthTolerance);
  let jambInnerOutline = polyLine2SetCCW(backProfile.opening2);
  let jambOuterOutline = offsetPolyLine2(polyLine2SetCW(polyLine2Copy(jambInnerOutline)), -WINDOW_JAMB_THICKNESS);

  // @todo(Andrew): this is a temporary fix until mirroring is ironed out for openings
  if (unit.mirror) {
    const aabb2 = polyLine2AABB2(backProfile.openingRect2);
    const centerX = (aabb2.maxX + aabb2.minX) / 2;
    const mirror2 = (p: PolyLine2) => {
      p = polyLine2ReverseCopy(p); // Line needs to be reversed so that the reflection still has correct vertex order.
      polyLine2Reflect(p, ray2New(centerX, 0, 0, 1));
      return p;
    };
    jambInnerOutline = mirror2(jambInnerOutline);
    jambOuterOutline = mirror2(jambOuterOutline);
  }

  const jambFrame = faceNew([jambOuterOutline, jambInnerOutline], jambPlane);
  const jambExtrusion = extrudeFaceAlongNormal(jambFrame, -(jambDepth - jambDepthTolerance * 2));

  /**
   * @TODO - users probably expect to:
   * - see the trim color on either side of the window be reflected by the interior/exterior nature of each side
   * - be able to override the trim material on either side.
   */
  const isExterior = wallOpeningHasExteriorSide(opening);
  if (isExterior) {
    unit.exterior.add(
      jambExtrusion,
      exteriorMaterialOverrideNew("trimExterior0", undefined, undefined, undefined, WINDOW_COMPONENT_JAMB),
    );
  } else {
    unit.interior.add(
      jambExtrusion,
      interiorMaterialOverrideNew("trimInterior0", undefined, undefined, WINDOW_COMPONENT_JAMB),
    );
  }
}

function windowUnitAddInteriorWindowFrame(
  unit: WindowUnit,
  shapes: OpeningShapes,
  interiorFacePlane: Plane,
  viewEntityDescriptor: ViewEntityDescriptor,
) {
  let { outline } = shapes;

  if (!outline || !interiorFacePlane) {
    return;
  }

  outline = profile2OrientCopy(polyLine2Reverse(polyLine2NonUniformScaleAndKeepCenter(outline, -1, 1)));
  const outerLoop = profile2OrientCopy(
    polyLine2Reverse(offsetPolyLine2SSkFallback(outline, WINDOW_INTERIOR_FRAME_WIDTH)),
  );

  const interiorWindowFrame = faceNew([outerLoop, outline], interiorFacePlane);

  const frameExtrusion = extrudeFaceAlongNormal(interiorWindowFrame, WINDOW_EXTERIOR_FRAME_DEPTH);
  unit.interior.add(
    frameExtrusion,
    interiorMaterialOverrideNew("trimInterior0", undefined, viewEntityDescriptor, WINDOW_COMPONENT_INNER_FRAME),
  );
}

/**
 * Decomposes a glass extrusion into cap faces (toward exterior / toward interior)
 * and side faces, adding them to the appropriate WindowUnit fields.
 */
function addGlassExtrusionToUnit(
  extrusion: Extrusion,
  towardExteriorNX: number,
  towardExteriorNY: number,
  towardExteriorNZ: number,
  unit: WindowUnit,
  exportComponentName: string,
): void {
  const brep = extrusionToBRep(extrusion);
  const { faces } = brep;

  if (faces.length < 2) {
    return; // Degenerate brep, ignore
  }

  // faces[0] = bottom cap (reversed profile), faces[1] = top cap (moved profile), rest = sides
  const [cap0, cap1, ...sideFaces] = faces;

  const [n0x, n0y, n0z] = planeNormal(cap0.plane);
  const dot0 = n0x * towardExteriorNX + n0y * towardExteriorNY + n0z * towardExteriorNZ;

  if (dot0 > 0) {
    unit.glassTowardExterior.faces.push(cap0);
    unit.glassTowardExterior.exportComponentNames.push(exportComponentName);
    unit.glassTowardInterior.faces.push(cap1);
    unit.glassTowardInterior.exportComponentNames.push(exportComponentName);
  } else {
    unit.glassTowardExterior.faces.push(cap1);
    unit.glassTowardExterior.exportComponentNames.push(exportComponentName);
    unit.glassTowardInterior.faces.push(cap0);
    unit.glassTowardInterior.exportComponentNames.push(exportComponentName);
  }

  unit.sideFaces.push(...sideFaces);
  unit.sideFaceExportComponentNames.push(...sideFaces.map(() => exportComponentName));
}

export function windowUnitNew(
  windowPlacement: GeneratedWindowPlacement,
  streetOrientation: StreetOrientation,
  depthAlignmentOffset: number = 0,
  interiorRoom: Room | undefined,
  exteriorRoom: Room | undefined,
  viewNotes: AllWindowViewNoteTable,
  context: WallSideContext,
  mirror: boolean,
  glazingColor: GlazingColor | undefined,
  entityHandleForSelectingReportingUnit?: DocEntityHandle,
  fallbackInstanceId?: string,
): WindowUnit {
  if (windowPlacement.handle) {
    BuildingGenerationContext.indexAddNode(windowPlacement.handle, windowPlacement.opening?.aabb3 ?? undefined);
  }

  const instanceId = interiorRoom?.instanceId ?? exteriorRoom?.instanceId ?? fallbackInstanceId;

  return {
    ...buildingEntityBaseNew("WINDOW", uid(), windowPlacement.handle),
    ...(instanceId
      ? {
          pickHandle: { type: "COMPONENT_INSTANCE", id: instanceId },
        }
      : {}),
    windowPlacement,
    streetOrientation,
    context,
    depthAlignmentOffset,
    interiorRoom,
    exteriorRoom,
    interior: new GeomTagGroup(),
    exterior: new GeomTagGroup(),
    grids: new GeomTagGroup(),
    decorations: new GeomTagGroup(),
    awningRoof: new GeomTagGroup(),
    props: [],
    arches: [],
    glassTowardExterior: { faces: [], exportComponentNames: [], towardExterior: true },
    glassTowardInterior: { faces: [], exportComponentNames: [], towardExterior: false },
    sideFaces: [],
    sideFaceExportComponentNames: [],
    screen: [],
    screenExportComponentNames: [],
    viewNotes,
    mirror,
    glazingColor,
    entityHandleForSelectingReportingUnit,
  };
}
